/* ------------------------------------------------------------------ */
/* Estate Agent Commission Calculator — defaults and logic             */
/* ------------------------------------------------------------------ */

export interface CommissionInputs {
  salePrice: number
  feePercent: number
  includeVat: boolean
  propertiesPerMonth: number
}

export interface CommissionResult {
  feeExVat: number
  feeIncVat: number
  vatAmount: number
  monthlyRevenue: number
  annualRevenue: number
  comparisonFeePercent: number
  comparisonFeeExVat: number
  comparisonAnnualRevenue: number
  annualDifference: number
}

export const COMMISSION_DEFAULTS: CommissionInputs = {
  salePrice: 350_000,
  feePercent: 1.2,
  includeVat: true,
  propertiesPerMonth: 4,
} as const

const VAT_RATE = 0.2
const UK_AVERAGE_FEE = 1.18 // Avg UK estate agent fee exc. VAT

export { UK_AVERAGE_FEE }

export function calculateCommission(inputs: CommissionInputs): CommissionResult {
  const feeExVat = inputs.salePrice * (inputs.feePercent / 100)
  const vatAmount = feeExVat * VAT_RATE
  const feeIncVat = feeExVat + vatAmount

  const revenuePerSale = inputs.includeVat ? feeIncVat : feeExVat
  const monthlyRevenue = revenuePerSale * inputs.propertiesPerMonth
  const annualRevenue = monthlyRevenue * 12

  // Comparison: +0.1% fee increase
  const comparisonFeePercent = inputs.feePercent + 0.1
  const comparisonFeeExVat = inputs.salePrice * (comparisonFeePercent / 100)
  const comparisonRevenuePerSale = inputs.includeVat
    ? comparisonFeeExVat * (1 + VAT_RATE)
    : comparisonFeeExVat
  const comparisonAnnualRevenue = comparisonRevenuePerSale * inputs.propertiesPerMonth * 12
  const annualDifference = comparisonAnnualRevenue - annualRevenue

  return {
    feeExVat: Math.round(feeExVat),
    feeIncVat: Math.round(feeIncVat),
    vatAmount: Math.round(vatAmount),
    monthlyRevenue: Math.round(monthlyRevenue),
    annualRevenue: Math.round(annualRevenue),
    comparisonFeePercent: Math.round(comparisonFeePercent * 10) / 10,
    comparisonFeeExVat: Math.round(comparisonFeeExVat),
    comparisonAnnualRevenue: Math.round(comparisonAnnualRevenue),
    annualDifference: Math.round(annualDifference),
  }
}

export { formatCurrency } from "@/lib/format-currency"
